import { Calendar, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { getInterviewerColor } from '@/lib/interviewerColors';
import { cn } from '@/lib/utils';
import { downloadBookingIcs, listBookingsForSlot } from '@/services/schedulingService';

import type { BookingStatus, InterviewSlot, SlotBooking } from '@/types/scheduling.types';

interface SlotDetailsDrawerProps {
  isOpen: boolean;
  slot: InterviewSlot | null;
  onClose: () => void;
  onDelete: (slotId: string) => Promise<void>;
  onMarkOutcome: (bookingId: string, outcome: 'ATTENDED' | 'NOSHOW') => Promise<void>;
}

const BOOKING_STATUS_COLORS: Record<BookingStatus, string> = {
  SCHEDULED: 'bg-green-100 text-green-800',
  RESCHEDULED: 'bg-amber-100 text-amber-800',
  CANCELLED: 'bg-red-100 text-red-800',
  NOSHOW: 'bg-gray-100 text-gray-800',
  ATTENDED: 'bg-blue-100 text-blue-800',
};

export function SlotDetailsDrawer({ isOpen, slot, onClose, onDelete, onMarkOutcome }: SlotDetailsDrawerProps) {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [bookings, setBookings] = useState<SlotBooking[]>([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [downloadingIcsId, setDownloadingIcsId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !slot) {
      setBookings([]);
      return;
    }
    const load = async () => {
      setIsLoadingBookings(true);
      try {
        const data = await listBookingsForSlot(slot.slotId);
        setBookings(data);
      } catch {
        setBookings([]);
      } finally {
        setIsLoadingBookings(false);
      }
    };
    void load();
  }, [isOpen, slot]);

  const handleDownloadIcs = useCallback(
    async (bookingId: string) => {
      setDownloadingIcsId(bookingId);
      try {
        const blob = await downloadBookingIcs(bookingId);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'interview.ics';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        toast.error(t('scheduling.toasts.error'));
      } finally {
        setDownloadingIcsId(null);
      }
    },
    [t],
  );

  const handleDelete = useCallback(async () => {
    if (!slot) {
      return;
    }
    setIsDeleting(true);
    try {
      await onDelete(slot.slotId);
    } finally {
      setIsDeleting(false);
    }
  }, [slot, onDelete]);

  const hasActiveBookings = bookings.some((b) => b.status !== 'CANCELLED');
  const bookedCount = bookings.filter((b) => b.status !== 'CANCELLED').length;

  const interviewerColor = slot ? getInterviewerColor(slot.interviewerUserId) : null;

  return createPortal(
    <>
      {/* Overlay */}
      {isOpen && <div className='fixed inset-0 z-40 bg-black/30' onClick={onClose} aria-hidden='true' />}

      {/* Drawer panel */}
      <div
        className={cn(
          'fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-xl transition-transform duration-300',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
        role='dialog'
        aria-modal='true'
        aria-label={t('scheduling.details.title')}>
        {/* Header */}
        <div className='flex items-center justify-between border-b px-6 py-4'>
          <h2 className='text-lg font-semibold text-gray-900'>{t('scheduling.details.title')}</h2>
          <button
            type='button'
            onClick={onClose}
            className='rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
            aria-label={t('scheduling.details.close')}>
            <X className='h-5 w-5' />
          </button>
        </div>

        {/* Body */}
        <div className='flex-1 overflow-y-auto px-6 py-4'>
          {slot && (
            <div className='space-y-5'>
              {/* Interviewer */}
              <div>
                <p className='text-sm font-medium text-gray-500'>{t('scheduling.details.interviewer')}</p>
                <div className='mt-1 flex items-center gap-2'>
                  {interviewerColor && (
                    <div className='h-2 w-2 rounded-full' style={{ backgroundColor: interviewerColor.hex }} />
                  )}
                  <span className='text-sm text-gray-900'>{slot.interviewerName}</span>
                </div>
              </div>

              {/* Time */}
              <div>
                <p className='text-sm font-medium text-gray-500'>{t('scheduling.details.time')}</p>
                <p className='mt-1 text-sm text-gray-900'>{new Date(slot.startAtUtc).toLocaleDateString()}</p>
                <p className='text-sm text-gray-600'>
                  {new Date(slot.startAtUtc).toLocaleTimeString()} &ndash;{' '}
                  {new Date(slot.endAtUtc).toLocaleTimeString()}
                </p>
              </div>

              {/* Capacity */}
              <div>
                <p className='text-sm font-medium text-gray-500'>{t('scheduling.details.capacity')}</p>
                <p className='mt-1 text-sm text-gray-900'>
                  {t('scheduling.details.booked', { count: bookedCount, capacity: slot.capacity })}
                </p>
              </div>

              {/* Notes */}
              {slot.notes && (
                <div>
                  <p className='text-sm font-medium text-gray-500'>{t('scheduling.details.notes')}</p>
                  <p className='mt-1 text-sm text-gray-900 whitespace-pre-wrap'>{slot.notes}</p>
                </div>
              )}

              {/* Bookings */}
              <div>
                <p className='text-sm font-medium text-gray-500'>
                  {t('scheduling.details.bookings')} ({bookings.length})
                </p>
                {isLoadingBookings ? (
                  <div className='mt-2 flex justify-center'>
                    <div className='h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent' />
                  </div>
                ) : bookings.length === 0 ? (
                  <p className='mt-2 text-sm text-gray-400'>{t('scheduling.details.noBookings')}</p>
                ) : (
                  <ul className='mt-2 space-y-3'>
                    {bookings.map((booking) => (
                      <li key={booking.bookingId} className='rounded-lg border border-gray-200 px-3 py-2'>
                        <div className='flex items-center justify-between'>
                          <span className='text-sm font-medium text-gray-900'>{booking.applicantName}</span>
                          <span
                            className={cn(
                              'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                              BOOKING_STATUS_COLORS[booking.status],
                            )}>
                            {t(`scheduling.bookingStatuses.${booking.status}`)}
                          </span>
                        </div>
                        <p className='mt-1 text-xs text-gray-500'>{new Date(booking.bookedAt).toLocaleDateString()}</p>
                        <div className='mt-2 flex flex-wrap gap-1.5'>
                          {(booking.status === 'SCHEDULED' || booking.status === 'RESCHEDULED') && (
                            <>
                              <button
                                type='button'
                                onClick={() => void onMarkOutcome(booking.bookingId, 'ATTENDED')}
                                className='inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 hover:bg-blue-100'>
                                {t('scheduling.attendance.markAttended')}
                              </button>
                              <button
                                type='button'
                                onClick={() => void onMarkOutcome(booking.bookingId, 'NOSHOW')}
                                className='inline-flex items-center gap-1 rounded bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-100'>
                                {t('scheduling.attendance.markNoShow')}
                              </button>
                            </>
                          )}
                          <button
                            type='button'
                            disabled={downloadingIcsId === booking.bookingId}
                            onClick={() => void handleDownloadIcs(booking.bookingId)}
                            className='inline-flex items-center gap-1 rounded bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50'>
                            <Calendar className='h-3 w-3' />
                            {downloadingIcsId === booking.bookingId
                              ? t('scheduling.attendance.downloadingIcs')
                              : t('scheduling.details.downloadIcs')}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {slot && (
          <div className='border-t px-6 py-4'>
            <div className='relative'>
              <button
                type='button'
                onClick={() => void handleDelete()}
                disabled={hasActiveBookings ? true : isDeleting}
                className='w-full rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50'>
                {isDeleting ? t('scheduling.details.deleting') : t('scheduling.details.delete')}
              </button>
              {hasActiveBookings && (
                <p className='mt-1 text-center text-xs text-gray-500'>{t('scheduling.details.cannotDelete')}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}
